import React, { useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';

import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { FormFieldType } from '@components/dynamic-form/types/inputTypes';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { IBaseDrawerProps } from '@common/types';
import { useGetDistinctEffectiveDates } from '@common/slices';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { RootState } from '@core/redux/store';
import { useSelector } from 'react-redux';

const ModifyCurrencyRateDrawer: React.FC<IBaseDrawerProps> = ({
  onSuccess
}) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const { data } = useGetDistinctEffectiveDates.useQuery();

  const selectOptions = useMemo<Record<string, string>>(() => {
    if (!data?.core?.GetDistinctEffectiveDates) return {};

    return data.core.GetDistinctEffectiveDates.reduce(
      (acc: Record<string, string>, date: string) => {
        acc[date] = new Date(date).toLocaleString();

        return acc;
      },
      {}
    );
  }, [data]);

  /** ----------------------------------------
   * Dynamic form sections
   * ---------------------------------------- */
  const sections = useMemo<IDynamicFormWidgetSection[]>(() => {
    if (!open || Object.keys(selectOptions).length === 0) return [];

    return [
      {
        inputs: {
          effectiveDate: {
            name: 'effectiveDate',
            title: 'Effective Date',
            placeholder: 'Effective Date',
            type: FormFieldType.select,
            value: '',
            required: true,
            disabled: false,
            selectOptions
          }
        }
      }
    ];
  }, [open, selectOptions]);

  /** ----------------------------------------
   * Submit handler
   * ---------------------------------------- */
  const onSubmit = async (values: Record<string, any>) => {
    try {
      const [formData] = normalizeDynamicFormDrawerValues(sections, values);
      if (!formData?.effectiveDate) {
        throw new Error('Please select an effective date.');
      }
      onSuccess('/configuration/currency-exchange-rate/add-ons', {
        state: { effectiveDate: formData.effectiveDate, mode: 'modify' }
      });
    } catch (error) {
      console.error(error);
      toast.error(<ToastErrorMessage>{error.message}</ToastErrorMessage>);
    }
  };

  return (
    <DynamicFormWidget
      sections={sections}
      onSubmit={onSubmit}
      submitButtonState={submitButtonState}
    />
  );
};

export { ModifyCurrencyRateDrawer };
